import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X, Loader2, Plus, ChevronDown, ChevronRight, ChevronLeft, Info, Check, Search,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adUnitsApi } from '../../lib/api/adUnits'
import {
  adUnitCatalogApi,
  invalidateCatalog,
  type AdUnitCatalog,
  type CatalogContentType,
  type CatalogSize,
} from '../../lib/api/adUnitCatalog'

interface CreateAdUnitModalProps {
  isOpen: boolean
  campaignId: number
  /**
   * Required to fetch the per-advertiser content-type + size allowlist.
   * Until this is known, the modal renders a loading state and disables
   * the Create button — we deliberately do not fall back to a hard-coded
   * list because the server is the source of truth.
   */
  advertiserId: number | null
  onClose: () => void
  onCreated: (adUnitId: number) => void
}

const SOCIAL_PROVIDERS = [
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'linkedin', label: 'LinkedIn' },
]

const TILES_TYPES = new Set(['tiles', 'tiles_format'])
const TEMPLATE_TYPES = new Set(['custom_template', 'parallax_template'])

export function CreateAdUnitModal({ isOpen, campaignId, advertiserId, onClose, onCreated }: CreateAdUnitModalProps) {
  const [name, setName] = useState('')
  const [contentType, setContentType] = useState<string>('')
  const [sizes, setSizes] = useState<Set<string>>(new Set())

  const [searchQuery, setSearchQuery] = useState('')

  const [mainContentType, setMainContentType] = useState('')
  const [socialProvider, setSocialProvider] = useState<string>('facebook')
  const [providerUsername, setProviderUsername] = useState('')

  const [showAdvanced, setShowAdvanced] = useState(false)
  const [sponsoredLabel, setSponsoredLabel] = useState('')
  const [sponsorUrl, setSponsorUrl] = useState('')
  const [autoOptimization, setAutoOptimization] = useState(false)
  const [hasUtm, setHasUtm] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  // Catalog (advertiser allowlist of content types + sizes) — drives the picker UI.
  const [catalog, setCatalog] = useState<AdUnitCatalog | null>(null)
  const [isCatalogLoading, setIsCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const sizesSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setName('')
      setContentType('')
      setSizes(new Set())
      setSearchQuery('')
      setMainContentType('')
      setSocialProvider('facebook')
      setProviderUsername('')
      setShowAdvanced(false)
      setSponsoredLabel('')
      setSponsorUrl('')
      setAutoOptimization(false)
      setHasUtm(false)
      setIsSubmitting(false)
    }
  }, [isOpen])

  // Fetch catalog when the modal opens (or the advertiser changes). The
  // catalog API caches per-advertiser, so reopening the modal in the same
  // session is free. We deliberately re-run the effect when advertiserId
  // changes so switching campaigns/advertisers picks up the right allowlist.
  const loadCatalog = useCallback(async (force = false) => {
    if (!advertiserId) return
    setIsCatalogLoading(true)
    setCatalogError(null)
    try {
      if (force) invalidateCatalog(advertiserId)
      const result = await adUnitCatalogApi.getCatalog(advertiserId)
      setCatalog(result)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load ad unit catalog'
      setCatalogError(message)
      setCatalog(null)
    } finally {
      setIsCatalogLoading(false)
    }
  }, [advertiserId])

  useEffect(() => {
    if (!isOpen || !advertiserId) return
    void loadCatalog()
  }, [isOpen, advertiserId, loadCatalog])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen, isSubmitting, onClose])

  useEffect(() => {
    if (contentType && sizesSectionRef.current) {
      sizesSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [contentType])

  const toggleSize = (size: string) => {
    setSizes(prev => {
      const next = new Set(prev)
      if (next.has(size)) next.delete(size)
      else next.add(size)
      return next
    })
  }

  const removeSize = (size: string) => {
    setSizes(prev => {
      const next = new Set(prev)
      next.delete(size)
      return next
    })
  }

  const addSize = (size: string) => {
    setSizes(prev => {
      const next = new Set(prev)
      next.add(size)
      return next
    })
  }

  const addSizes = (list: string[]) => {
    setSizes(prev => {
      const next = new Set(prev)
      list.forEach(s => next.add(s))
      return next
    })
  }

  const clearSizes = () => setSizes(new Set())

  const q = searchQuery.trim().toLowerCase()

  // Group catalog content types into rows by `group`, preserving server
  // order both within and across groups. Falls back to "Other" for
  // ungrouped entries so they never disappear silently.
  const allTypeRows = useMemo<Array<{ title: string; types: CatalogContentType[] }>>(() => {
    if (!catalog) return []
    const groupOrder: string[] = []
    const groups = new Map<string, CatalogContentType[]>()
    catalog.content_types.forEach(t => {
      const key = t.group || 'Other'
      if (!groups.has(key)) {
        groups.set(key, [])
        groupOrder.push(key)
      }
      groups.get(key)!.push(t)
    })
    return groupOrder.map(title => ({ title, types: groups.get(title)! }))
  }, [catalog])

  const filteredTypeRows = useMemo(() => {
    if (!q) return allTypeRows
    return allTypeRows
      .map(row => ({
        ...row,
        types: row.types.filter(t =>
          t.label.toLowerCase().includes(q) ||
          t.value.toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
        ),
      }))
      .filter(row => row.types.length > 0)
  }, [q, allTypeRows])

  // Quick lookup: size string -> CatalogSize (for selected-chip labels).
  const presetIndex = useMemo(() => {
    const map = new Map<string, CatalogSize>()
    catalog?.sizes.forEach(s => map.set(s.size, s))
    return map
  }, [catalog])

  const selectedContentType = useMemo<CatalogContentType | null>(() => {
    if (!catalog) return null
    return catalog.content_types.find(t => t.value === contentType) ?? null
  }, [catalog, contentType])

  const selectedTypeLabel = selectedContentType?.label ?? null

  // Per-type allowlist of sizes. If the type doesn't declare one, fall back
  // to the full catalog list — that's the "no restriction" case the server
  // signals by omitting `allowed_sizes`.
  const sizesForSelectedType = useMemo<CatalogSize[]>(() => {
    if (!catalog) return []
    if (!selectedContentType) return catalog.sizes
    const allow = selectedContentType.allowed_sizes
    if (!allow || allow.length === 0) return catalog.sizes
    const allowSet = new Set(allow)
    // Preserve catalog order; tack on any allow-list entries that aren't in
    // the global sizes array (custom-only sizes) so they still render.
    const knownAllowed = catalog.sizes.filter(s => allowSet.has(s.size))
    const known = new Set(knownAllowed.map(s => s.size))
    const extras: CatalogSize[] = []
    allow.forEach(s => {
      if (known.has(s)) return
      const m = s.match(/^\s*(\d+)\s*[x×]\s*(\d+)\s*$/i)
      if (m) extras.push({ size: `${m[1]}x${m[2]}`, w: parseInt(m[1], 10), h: parseInt(m[2], 10) })
    })
    return [...knownAllowed, ...extras]
  }, [catalog, selectedContentType])

  // When the user switches content type, drop any previously selected sizes
  // that are no longer in the new allowlist and let them know.
  const lastTypeRef = useRef<string>('')
  useEffect(() => {
    if (!contentType || !catalog || !selectedContentType) {
      lastTypeRef.current = contentType
      return
    }
    if (lastTypeRef.current === contentType) return
    lastTypeRef.current = contentType
    const allow = selectedContentType.allowed_sizes
    if (!allow || allow.length === 0) return
    const allowSet = new Set(allow)
    let removed = 0
    setSizes(prev => {
      let mutated = false
      const next = new Set<string>()
      prev.forEach(s => {
        if (allowSet.has(s)) {
          next.add(s)
        } else {
          mutated = true
          removed += 1
        }
      })
      return mutated ? next : prev
    })
    if (removed > 0) {
      toast(
        `Removed ${removed} size${removed === 1 ? '' : 's'} not compatible with ${selectedContentType.label}`,
        { icon: 'ℹ️' },
      )
    }
  }, [contentType, catalog, selectedContentType])

  const isSocial = contentType === 'social_display_ad'
  const isTemplate = TEMPLATE_TYPES.has(contentType)
  const isTiles = TILES_TYPES.has(contentType)

  const canSubmit =
    !!catalog &&
    !catalogError &&
    name.trim().length > 0 &&
    name.trim().length <= 128 &&
    contentType.length > 0 &&
    sizes.size > 0 &&
    (!isSocial || providerUsername.trim().length > 0) &&
    !isSubmitting

  const handleSubmit = async () => {
    if (!canSubmit) return

    const payload: Record<string, any> = {
      name: name.trim(),
      content_type: contentType,
      sizes: Array.from(sizes),
    }

    if (isTemplate && mainContentType.trim()) {
      payload.main_content_type = mainContentType.trim()
    }

    if (isSocial) {
      payload.social_media_provider = socialProvider
      payload.provider_username = providerUsername.trim()
    }

    if (sponsoredLabel.trim()) payload.sponsored_label = sponsoredLabel.trim()
    if (sponsorUrl.trim()) payload.sponsor_url = sponsorUrl.trim()
    if (autoOptimization) payload.auto_optimization = true
    if (hasUtm) payload.has_utm = true

    setIsSubmitting(true)
    try {
      const adUnit = await adUnitsApi.createAdUnit(campaignId, payload)
      toast.success('Ad unit created successfully')
      const newId = adUnit.ad_unit_id || adUnit.id
      onCreated(newId)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create ad unit'
      const lower = message.toLowerCase()
      if (lower.includes('forbidden') || lower.includes('permission') || lower.includes('does not have access')) {
        toast.error(message || "You don't have permission to create ad units")
      } else {
        toast.error(message)
      }
      setIsSubmitting(false)
    }
  }

  const hintMessage = !canSubmit && !isSubmitting
    ? (name.trim().length === 0 ? 'Name required'
      : !contentType ? 'Pick a content type'
      : sizes.size === 0 ? 'Pick at least one size'
      : (isSocial && !providerUsername.trim()) ? 'Provider username required'
      : '')
    : ''

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
          onClick={() => !isSubmitting && onClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-gray-950 rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white"
          >
            {/* Hero header */}
            <div className="relative flex-shrink-0 px-8 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="min-w-0">
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase">New ad unit</div>
                  <h2 className="text-2xl font-bold mt-1">Start building your ad unit</h2>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Name it, pick a content type, then choose sizes. You can refine everything in Creative Studio.</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-500" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search content types"
                      className="w-60 pl-9 pr-3 py-1.5 text-xs rounded-full border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/80 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="h-6 w-px bg-gray-200 dark:bg-gray-800" />
                  <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    aria-label="Close"
                    title="Close"
                    className="h-9 w-9 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white flex items-center justify-center transition-colors shadow-sm disabled:opacity-50"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </div>
              </div>

              {/* Name field */}
              <div>
                <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                  Name <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Name your ad unit"
                    maxLength={128}
                    className="flex-1 px-4 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="text-[11px] text-gray-500 whitespace-nowrap">{name.trim().length}/128</div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto scrollbar-thin">
              <div className="py-6 space-y-7">
                {/* Catalog loading / error states — replace content-type +
                    sizes sections only; header/footer stay visible so the
                    user keeps context and can cancel. */}
                {!advertiserId ? (
                  <div className="px-8">
                    <div className="rounded-xl border border-dashed border-amber-300 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/10 px-5 py-6 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Advertiser not loaded yet</div>
                        <div className="text-xs mt-1">
                          We need an advertiser to fetch the list of content types and sizes for this campaign.
                          Try reopening this dialog once the campaign has finished loading.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isCatalogLoading && !catalog ? (
                  <div className="px-8 py-16 flex flex-col items-center justify-center gap-3">
                    <Loader2 className="h-7 w-7 text-primary-600 dark:text-primary-400 animate-spin" />
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading content types and sizes…</p>
                  </div>
                ) : catalogError ? (
                  <div className="px-8">
                    <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/70 dark:bg-red-900/10 px-5 py-5 flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-red-700 dark:text-red-300">
                          Couldn’t load the ad unit catalog
                        </div>
                        <div className="text-xs text-red-700/80 dark:text-red-300/80 mt-1 break-words">
                          {catalogError}
                        </div>
                        <button
                          type="button"
                          onClick={() => void loadCatalog(true)}
                          className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-500 text-white transition-colors"
                        >
                          {isCatalogLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                          Try again
                        </button>
                      </div>
                    </div>
                  </div>
                ) : catalog ? (
                  <>
                    {/* Content type rows */}
                    <section>
                      <div className="flex items-center justify-between px-8 mb-3">
                        <h3 className="text-sm font-semibold">
                          Content type <span className="text-red-500">*</span>
                          {selectedTypeLabel && (
                            <span className="ml-2 text-[11px] font-medium text-primary-600 dark:text-primary-400">
                              {selectedTypeLabel}
                            </span>
                          )}
                        </h3>
                      </div>

                      {allTypeRows.length === 0 ? (
                        <div className="px-8 text-xs text-gray-500 py-4">
                          No content types are available for this advertiser.
                        </div>
                      ) : filteredTypeRows.length === 0 ? (
                        <div className="px-8 text-xs text-gray-500 py-4">No content types match your search.</div>
                      ) : (
                        <div className="space-y-5">
                          {filteredTypeRows.map(row => (
                            <div key={row.title}>
                              <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-500 mb-2 px-8">
                                {row.title}
                              </div>
                              <HorizontalRow>
                                {row.types.map(t => (
                                  <TypeCard
                                    key={`${row.title}-${t.value}`}
                                    type={t}
                                    active={contentType === t.value}
                                    onClick={() => setContentType(t.value)}
                                  />
                                ))}
                              </HorizontalRow>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>

                    {/* Sizes section — appears after content type is picked */}
                    <section ref={sizesSectionRef} className="px-8">
                      {!contentType ? (
                        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/30 px-5 py-4 text-xs text-gray-500">
                          Pick a content type above to choose compatible sizes.
                        </div>
                      ) : (
                        <SizesPanel
                          sizes={sizes}
                          presets={sizesForSelectedType}
                          presetIndex={presetIndex}
                          onToggle={toggleSize}
                          onRemove={removeSize}
                          onAdd={addSize}
                          onAddMany={addSizes}
                          onClearAll={clearSizes}
                        />
                      )}
                    </section>
                  </>
                ) : null}

                {/* Conditional sections + advanced — hidden until the
                    catalog is in hand so users don't see template / social
                    fields when there's nothing to pair them with. */}
                {catalog && (
                <div className="px-8 space-y-5">
                  {isTiles && (
                    <div className="flex gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>
                        This type requires feature images &mdash; upload them in Creative Studio after creating the ad unit.
                      </span>
                    </div>
                  )}

                  {isTemplate && (
                    <div>
                      <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                        Main Content Type <span className="text-gray-500 font-normal normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={mainContentType}
                        onChange={e => setMainContentType(e.target.value)}
                        placeholder="e.g. custom_template"
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  {isSocial && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">Social Provider</label>
                        <select
                          value={socialProvider}
                          onChange={e => setSocialProvider(e.target.value)}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                          {SOCIAL_PROVIDERS.map(p => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">
                          Provider Username <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={providerUsername}
                          onChange={e => setProviderUsername(e.target.value)}
                          placeholder="e.g. example_user"
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      className="flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                      {showAdvanced ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      Advanced settings
                    </button>

                    {showAdvanced && (
                      <div className="mt-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">Sponsored Label</label>
                            <input
                              type="text"
                              value={sponsoredLabel}
                              onChange={e => setSponsoredLabel(e.target.value)}
                              placeholder="e.g. Sponsored"
                              maxLength={36}
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold tracking-wider uppercase text-gray-600 dark:text-gray-400 mb-1.5">Sponsor URL</label>
                            <input
                              type="text"
                              value={sponsorUrl}
                              onChange={e => setSponsorUrl(e.target.value)}
                              placeholder="https://example.com"
                              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={autoOptimization}
                              onChange={e => setAutoOptimization(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-primary-600 focus:ring-primary-500"
                            />
                            Auto optimization
                          </label>
                          <label className="inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={hasUtm}
                              onChange={e => setHasUtm(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-primary-600 focus:ring-primary-500"
                            />
                            Has UTM
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-8 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
              <div className="min-w-0 text-xs text-gray-600 dark:text-gray-400 flex items-center gap-3">
                <span>
                  <span className="font-medium text-gray-900 dark:text-white">{sizes.size}</span> size{sizes.size === 1 ? '' : 's'}
                </span>
                <span className="text-gray-300 dark:text-gray-700">&bull;</span>
                <span>
                  {selectedTypeLabel ? (
                    <span className="font-medium text-gray-900 dark:text-white">{selectedTypeLabel}</span>
                  ) : (
                    'no content type'
                  )}
                </span>
                {hintMessage && (
                  <>
                    <span className="text-gray-300 dark:text-gray-700">&bull;</span>
                    <span className="text-amber-600 dark:text-amber-400">{hintMessage}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5" />
                      Create ad unit
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

interface SizesPanelProps {
  sizes: Set<string>
  /**
   * The set of sizes the user is allowed to pick from for the currently
   * selected content type. Driven by the advertiser's catalog allowlist
   * (filtered to the type's `allowed_sizes` when present).
   */
  presets: CatalogSize[]
  /**
   * Lookup for the full catalog (not just the type-filtered list) so that
   * selected-chip labels still resolve when a previously-selected size is
   * temporarily filtered out by the current content type.
   */
  presetIndex: Map<string, CatalogSize>
  onToggle: (size: string) => void
  onRemove: (size: string) => void
  onAdd: (size: string) => void
  onAddMany: (sizes: string[]) => void
  onClearAll: () => void
}

function SizesPanel({ sizes, presets, presetIndex, onToggle, onRemove, onAdd, onAddMany, onClearAll }: SizesPanelProps) {
  const [query, setQuery] = useState('')
  const [customW, setCustomW] = useState('')
  const [customH, setCustomH] = useState('')
  const [error, setError] = useState('')

  const dragModeRef = useRef<null | 'add' | 'remove'>(null)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    const endDrag = () => {
      if (dragModeRef.current !== null) {
        dragModeRef.current = null
        setIsDragging(false)
      }
    }
    window.addEventListener('mouseup', endDrag)
    window.addEventListener('mouseleave', endDrag)
    return () => {
      window.removeEventListener('mouseup', endDrag)
      window.removeEventListener('mouseleave', endDrag)
    }
  }, [])

  const q = query.trim().toLowerCase()

  const filteredPresets = useMemo(() => {
    if (!q) return presets
    return presets.filter(p =>
      p.size.toLowerCase().includes(q) ||
      (p.label || '').toLowerCase().includes(q)
    )
  }, [q, presets])

  const allFilteredSelected =
    filteredPresets.length > 0 && filteredPresets.every(p => sizes.has(p.size))

  const handleDragStart = (size: string) => {
    if (sizes.has(size)) {
      dragModeRef.current = 'remove'
      onRemove(size)
    } else {
      dragModeRef.current = 'add'
      onAdd(size)
    }
    setIsDragging(true)
  }

  const handleDragOver = (size: string) => {
    const mode = dragModeRef.current
    if (!mode) return
    if (mode === 'add' && !sizes.has(size)) onAdd(size)
    else if (mode === 'remove' && sizes.has(size)) onRemove(size)
  }

  const handleAddCustom = () => {
    const w = parseInt(customW, 10)
    const h = parseInt(customH, 10)
    if (!w || !h || w <= 0 || h <= 0 || w > 9999 || h > 9999) {
      setError('Enter valid width and height (1-9999)')
      return
    }
    const size = `${w}x${h}`
    if (sizes.has(size)) {
      setError('Size already added')
      return
    }
    onAdd(size)
    setCustomW('')
    setCustomH('')
    setError('')
  }

  const handleCustomKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddCustom()
    }
  }

  const selectedArray = Array.from(sizes)

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 overflow-hidden">
      {/* Header with inline W × H add */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-baseline gap-2">
            <h3 className="text-sm font-semibold">
              Sizes <span className="text-red-500">*</span>
            </h3>
            {sizes.size > 0 && (
              <span className="text-[11px] font-medium text-primary-600 dark:text-primary-400">
                {sizes.size} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[11px]">
            <button
              type="button"
              onClick={() => {
                if (allFilteredSelected) {
                  filteredPresets.forEach(p => onRemove(p.size))
                } else {
                  onAddMany(filteredPresets.map(p => p.size))
                }
              }}
              disabled={filteredPresets.length === 0}
              className="px-2 py-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {allFilteredSelected ? 'Unselect all' : q ? `Select all (${filteredPresets.length})` : 'Select all'}
            </button>
            <span className="text-gray-300 dark:text-gray-700">|</span>
            <button
              type="button"
              onClick={onClearAll}
              disabled={sizes.size === 0}
              className="px-2 py-1 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-1 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/40">
            <input
              type="number"
              min={1}
              max={9999}
              value={customW}
              onChange={e => { setCustomW(e.target.value); setError('') }}
              onKeyDown={handleCustomKey}
              placeholder="Width"
              className="w-16 bg-transparent text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none px-2 py-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-gray-400 dark:text-gray-600 text-xs">&times;</span>
            <input
              type="number"
              min={1}
              max={9999}
              value={customH}
              onChange={e => { setCustomH(e.target.value); setError('') }}
              onKeyDown={handleCustomKey}
              placeholder="Height"
              className="w-16 bg-transparent text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none px-2 py-1 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
          <button
            type="button"
            onClick={handleAddCustom}
            disabled={!customW || !customH}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 hover:bg-primary-500 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="h-3 w-3" />
            Add custom
          </button>
        </div>
      </div>

      {error && (
        <div className="px-5 pt-2 text-[11px] text-red-500 dark:text-red-400">{error}</div>
      )}

      {/* Search + selected chips */}
      <div className="px-5 pt-4 space-y-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/40">
          <Search className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search preset sizes (e.g. 300, leaderboard, banner)"
            className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {selectedArray.length > 0 && (
          <div className="max-h-20 overflow-y-auto scrollbar-thin">
            <div className="flex flex-wrap gap-1.5">
              {selectedArray.map(size => {
                const preset = presetIndex.get(size)
                return (
                  <span
                    key={size}
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-primary-600 text-white"
                  >
                    <span className="font-mono">{size}</span>
                    {preset && <span className="text-primary-100/80 text-[10px]">· {preset.label}</span>}
                    <button
                      type="button"
                      onClick={() => onRemove(size)}
                      className="hover:bg-primary-700 rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )
              })}
            </div>
          </div>
        )}
      </div>

      {/* Visual grid of preset size cards */}
      <div className="px-5 py-4">
        {filteredPresets.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-gray-500">
            No presets match &quot;{query}&quot;. Add it as a custom size above.
          </div>
        ) : (
          <>
            <div
              onMouseLeave={() => { /* keep drag alive; global mouseup ends it */ }}
              className={`grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5 max-h-[280px] overflow-y-auto scrollbar-thin pr-1 ${
                isDragging ? 'select-none cursor-crosshair' : ''
              }`}
            >
              {filteredPresets.map(preset => (
                <SizeCard
                  key={preset.size}
                  preset={preset}
                  active={sizes.has(preset.size)}
                  onDragStart={() => handleDragStart(preset.size)}
                  onDragOver={() => handleDragOver(preset.size)}
                  onKeyToggle={() => onToggle(preset.size)}
                />
              ))}
            </div>
            <div className="mt-2 text-[11px] text-gray-500">
              Tip: click and drag across cards to select multiple at once.
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface SizeCardProps {
  preset: CatalogSize
  active: boolean
  onDragStart: () => void
  onDragOver: () => void
  onKeyToggle: () => void
}

function SizeCard({ preset, active, onDragStart, onDragOver, onKeyToggle }: SizeCardProps) {
  const maxDim = 56
  const ratio = preset.w / preset.h
  const boxW = ratio >= 1 ? maxDim : Math.max(12, Math.round(maxDim * ratio))
  const boxH = ratio >= 1 ? Math.max(12, Math.round(maxDim / ratio)) : maxDim

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onMouseDown={e => { e.preventDefault(); onDragStart() }}
      onMouseEnter={() => onDragOver()}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onKeyToggle()
        }
      }}
      className={`group relative flex flex-col items-center justify-between gap-1.5 p-2.5 rounded-lg border transition-all duration-150 cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500/60 ${
        active
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-inset ring-primary-500/60'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700'
      }`}
    >
      {active && (
        <div className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-primary-600 flex items-center justify-center shadow">
          <Check className="h-2.5 w-2.5 text-white" />
        </div>
      )}
      <div className="h-14 w-full flex items-center justify-center pointer-events-none">
        <div
          className={`rounded-sm border ${
            active ? 'border-primary-400 bg-primary-500/40' : 'border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
          }`}
          style={{ width: boxW, height: boxH }}
        />
      </div>
      <div className="w-full text-center pointer-events-none">
        <div className={`text-[11px] font-mono font-semibold ${active ? 'text-primary-700 dark:text-primary-100' : 'text-gray-900 dark:text-white'}`}>
          {preset.size}
        </div>
        <div className="text-[10px] text-gray-500 truncate">{preset.label}</div>
      </div>
    </div>
  )
}

function HorizontalRow({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canLeft, setCanLeft] = useState(false)
  const [canRight, setCanRight] = useState(false)

  const updateArrows = () => {
    const el = scrollRef.current
    if (!el) return
    setCanLeft(el.scrollLeft > 4)
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }

  useEffect(() => {
    updateArrows()
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', updateArrows, { passive: true })
    window.addEventListener('resize', updateArrows)
    return () => {
      el.removeEventListener('scroll', updateArrows)
      window.removeEventListener('resize', updateArrows)
    }
  }, [])

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current
    if (!el) return
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.8), behavior: 'smooth' })
  }

  return (
    <div className="relative group">
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scrollbar-thin px-8 py-3 snap-x scroll-px-8"
      >
        {children}
      </div>

      {/* Fade edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white dark:from-gray-950 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white dark:from-gray-950 to-transparent" />

      {/* Arrow buttons */}
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Scroll left"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white/90 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-white flex items-center justify-center shadow-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Scroll right"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-9 w-9 rounded-full bg-white/90 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-white flex items-center justify-center shadow-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-opacity opacity-0 group-hover:opacity-100 focus:opacity-100"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

interface TypeCardProps {
  type: CatalogContentType
  active: boolean
  onClick: () => void
}

function TypeCard({ type, active, onClick }: TypeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`snap-start flex-shrink-0 w-56 rounded-xl border text-left transition-all duration-150 overflow-hidden ${
        active
          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500/60'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/40 hover:bg-gray-50 dark:hover:bg-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-xl'
      }`}
    >
      <div className="relative h-24 bg-gradient-to-br from-primary-100 via-gray-50 to-gray-100 dark:from-primary-900/40 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        {active && (
          <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-primary-600 flex items-center justify-center shadow-lg">
            <Check className="h-3 w-3 text-white" />
          </div>
        )}
        <TypeThumb value={type.value} active={active} />
      </div>
      <div className="px-3 py-2.5">
        <div className={`text-sm font-semibold truncate ${active ? 'text-primary-700 dark:text-primary-200' : 'text-gray-900 dark:text-white'}`}>
          {type.label}
        </div>
        <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-0.5 truncate">{type.description || ''}</div>
      </div>
    </button>
  )
}

function TypeThumb({ value, active }: { value: string; active: boolean }) {
  const strong = active ? 'bg-primary-500 dark:bg-primary-400' : 'bg-gray-400'
  const soft = active ? 'bg-primary-400/60 dark:bg-primary-600/60' : 'bg-gray-300 dark:bg-gray-600'
  const border = active ? 'border-primary-500' : 'border-gray-400 dark:border-gray-600'

  const fillBox = active ? 'bg-primary-200 dark:bg-primary-700/60' : 'bg-gray-200 dark:bg-gray-700'
  if (value === 'carousel' || value === 'carousel_overlay' || value === 'carousel_skinny' || value === 'carousel_rotation') {
    return (
      <div className="flex items-center gap-1">
        <div className={`w-6 h-10 rounded-sm border ${border} ${soft}`} />
        <div className={`w-10 h-10 rounded-sm border-2 ${border} ${fillBox}`} />
        <div className={`w-6 h-10 rounded-sm border ${border} ${soft}`} />
      </div>
    )
  }
  if (value === 'list' || value === 'single_ad_scroll' || value === 'lead_gen_scroll') {
    return (
      <div className="flex flex-col gap-1">
        <div className={`w-16 h-2 rounded ${soft}`} />
        <div className={`w-16 h-2 rounded ${strong}`} />
        <div className={`w-16 h-2 rounded ${soft}`} />
        <div className={`w-16 h-2 rounded ${soft}`} />
      </div>
    )
  }
  if (value === 'single_ad') {
    return <div className={`w-20 h-12 rounded border-2 ${border} ${fillBox}`} />
  }
  if (value === 'tiles' || value === 'tiles_format') {
    return (
      <div className="grid grid-cols-2 gap-1">
        <div className={`w-7 h-7 rounded-sm ${soft}`} />
        <div className={`w-7 h-7 rounded-sm ${strong}`} />
        <div className={`w-7 h-7 rounded-sm ${strong}`} />
        <div className={`w-7 h-7 rounded-sm ${soft}`} />
      </div>
    )
  }
  if (value === 'vertical_video' || value === 'single_video_ad') {
    return (
      <div className={`w-9 h-14 rounded border-2 ${border} ${fillBox} flex items-center justify-center`}>
        <div className={`w-0 h-0 border-y-[6px] border-y-transparent border-l-[10px] ${active ? 'border-l-primary-600 dark:border-l-primary-300' : 'border-l-gray-500 dark:border-l-gray-300'}`} />
      </div>
    )
  }
  if (value === 'social_display_ad') {
    return (
      <div className={`w-16 h-14 rounded border-2 ${border} ${fillBox} p-1.5 flex flex-col gap-1`}>
        <div className={`h-2 rounded ${strong} w-3/4`} />
        <div className={`flex-1 rounded ${soft}`} />
      </div>
    )
  }
  if (value === 'spin') {
    return (
      <div className={`w-12 h-12 rounded-full border-2 border-dashed ${border} flex items-center justify-center`}>
        <div className={`w-4 h-4 rounded-full ${strong}`} />
      </div>
    )
  }
  if (value === 'parallax_gallery' || value === 'parallax_template' || value === 'mask_scroll') {
    return (
      <div className="flex gap-0.5 items-end">
        <div className={`w-2 h-8 rounded-sm ${soft}`} />
        <div className={`w-2 h-12 rounded-sm ${strong}`} />
        <div className={`w-2 h-6 rounded-sm ${soft}`} />
        <div className={`w-2 h-10 rounded-sm ${strong}`} />
        <div className={`w-2 h-8 rounded-sm ${soft}`} />
      </div>
    )
  }
  if (value === 'lead_gen' || value === 'smart_survey') {
    return (
      <div className={`w-20 h-14 rounded border-2 ${border} ${fillBox} p-1.5 flex flex-col gap-1`}>
        <div className={`h-1.5 w-full rounded ${soft}`} />
        <div className={`h-1.5 w-2/3 rounded ${soft}`} />
        <div className={`h-3 w-1/2 rounded mt-auto self-end ${strong}`} />
      </div>
    )
  }
  if (value === 'custom_template') {
    return (
      <div className={`w-16 h-12 rounded border-2 ${border} ${fillBox} flex items-center justify-center`}>
        <div className={`font-mono text-[10px] ${active ? 'text-primary-700 dark:text-primary-200' : 'text-gray-600 dark:text-gray-300'}`}>&lt;/&gt;</div>
      </div>
    )
  }
  return <div className={`w-14 h-10 rounded border-2 ${border} ${fillBox}`} />
}
